import { useQuery } from 'react-query';
import { auth } from '../sp-firebase/firebase';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

async function waitForFirebaseUser(): Promise<void> {
  if (auth.currentUser) return;
  await new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        unsubscribe();
        resolve(user);
      }
    });
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user');
  }
  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export interface CurvePoint {
  formulaId: string;
  formulaCode: string;
  date: string;
  price: number;
  month01?: number;
  month02?: number;
  month03?: number;
  month04?: number;
  month05?: number;
  month06?: number;
  month07?: number;
  month08?: number;
  month09?: number;
  month10?: number;
  month11?: number;
  month12?: number;
}

interface FormulaCurveResponse {
  success: boolean;
  history: CurvePoint[];
}

async function getFormulaCurve(formulaCode: string): Promise<CurvePoint[]> {
  await waitForFirebaseUser();
  const headers = await getAuthHeaders();
  
  const url = `${API_URL}/api/v2/formulas/${formulaCode}/history`;
  const response = await fetch(url, { headers });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch formula curve: ${response.statusText}`);
  }
  
  const data: FormulaCurveResponse = await response.json();
  return data.history || [];
}

export function useFormulaCurve(formulaCode: string | null) {
  return useQuery(
    ['formula-curve', formulaCode],
    () => getFormulaCurve(formulaCode!),
    {
      enabled: !!formulaCode,
      staleTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    }
  );
}
